import {
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Listen,
  Prop,
  State,
  Watch
} from '@stencil/core';
import {
  ToggleExpandEventType,
  TreeSelectConfigType,
  TreeviewNodeType
} from '../../models';
import { sortData } from '../../utils';

const defaultConfig: TreeSelectConfigType = {
  countLabel: 'Count',
  headingLabel: 'Name',
  hideSearch: false,
  hideSelectAll: false,
  name: '',
  searchLabel: 'Search',
  selectAllLabel: 'Select All',
  deselectAllLabel: 'Deselect All',
  searchPlaceholder: 'Search options',
  singleSelect: false,
  hideSort: false,
  sortKey: 'label',
  sortAsc: true,
  sortProperty: 'label'
};

@Component({
  tag: 'uui-tree-select',
  styleUrl: './uui-tree-select.css',
  shadow: false,
  scoped: true
})
export class UuiTreeSelect {
  /** items to display in tree */
  @Prop({ mutable: true, reflect: false }) items: TreeviewNodeType[] | string;
  /** config options for tree */
  @Prop({ mutable: true, reflect: false }) config:
    | TreeSelectConfigType
    | string = defaultConfig;
  /** sort key */
  @Prop({ mutable: true }) sortKey: string;
  /** sort items asc */
  @Prop({ mutable: true }) sortAsc: boolean;
  /** triggers when an item is checked. Passes an array for mutliselect and a single value for single select */
  @Event({ bubbles: true, composed: true }) check: EventEmitter<string[]>;
  /** triggers when the user types into the search box */
  @Event({ bubbles: true, composed: true }) searchChange: EventEmitter<string>;
  /** list of item values to mark as checked. */
  @Prop({ mutable: true, reflect: false }) checked: string[] | string = [];
  /** triggers when an item is expanded and passes back the expanded item */
  @Event({ bubbles: true, composed: true })
  toggleExpand: EventEmitter;
  @Element() el: HTMLElement;
  /** triggers when list of items is scrolled */
  @Event({ bubbles: true, composed: true })
  itemsScroll: EventEmitter;
  /** triggers when the user changes sort order */
  @Event({ bubbles: true, composed: true }) sortChange: EventEmitter;
  @State() searchText: string;
  @State() selectedItems: TreeviewNodeType[] = [];
  @State() clonedItemsForSearch: TreeviewNodeType[] = [];
  @State() currentItems: TreeviewNodeType[] = [];

  componentWillLoad() {
    // workaround for storybook where items needs to be passed as a string
    if (typeof this.items === 'string') {
      this.items = JSON.parse(this.items) as TreeviewNodeType[];
    }

    // workaround for storybook where config needs to be passed as a string
    if (typeof this.config === 'string') {
      this.config = JSON.parse(this.config as string);
    }

    let config = this.config as TreeSelectConfigType;

    // pulls in default config to set any values the consumer hasn't explicitly passed
    this.config = config = {
      ...defaultConfig,
      ...config
    };

    let sortedData = this.items;

    if (!config.hideSort) {
      this.sortAsc = config.sortAsc;
      this.sortKey = config.sortKey;

      sortedData = sortData(
        this.items,
        this.sortAsc ? 'asc' : 'desc',
        this.sortKey
      );
    }

    // clones passed in items to prevent them being modified
    this.updateCurrentItems(sortedData);

    // workaround for storybook where checked needs to be passed as a string
    var checkObj =
      typeof this.checked === 'string'
        ? (JSON.parse(this.checked as string) as string[])
        : this.checked;

    // sets up default checked state
    if (checkObj.length > 0) {
      this.setDefaultCheckedState(checkObj as string[], this.currentItems);
      this.updateParents(this.currentItems);
    }

    if (config.singleSelect && this.currentItems.find(c => c.children)) {
      throw Error(
        "Invalid config passed for single select. Children aren't valid for single select."
      );
    }

    // cloning items for searching on
    this.clonedItemsForSearch = JSON.parse(JSON.stringify(this.currentItems));
  }

  @Watch('items')
  itemsUpdated(newValue: string | TreeviewNodeType[]) {
    if (typeof newValue === 'string') {
      this.currentItems = JSON.parse(newValue);
      return;
    }
    this.currentItems = newValue as TreeviewNodeType[];
  }

  setDefaultCheckedState(
    selected: string[],
    items: TreeviewNodeType[],
    checked = false
  ) {
    items.forEach(item => {
      var itemChecked = checked ? checked : selected.includes(item.value);
      item.checked = itemChecked;

      if (item.children && item.children.length > 0) {
        this.setDefaultCheckedState(selected, item.children, itemChecked);
      }
    });
  }

  updateSelectedItems() {
    const getSelectedNodes = (result: TreeviewNodeType[], node: any) => {
      if (node.checked) {
        result.push(node);
        return result;
      }
      if (Array.isArray(node.children) && node.children?.length > 0) {
        const nodes = node.children.reduce(getSelectedNodes, []);
        if (nodes.length) result.push({ ...node, children: nodes });
      }
      return result;
    };
    // exclude readonly nodes from selected items
    this.selectedItems = this.clonedItemsForSearch.reduce(getSelectedNodes, []);
    this.updateCurrentItems(this.currentItems);
  }

  updateParents(items: TreeviewNodeType[]) {
    items?.forEach(item => {
      if (item?.children?.length > 0) {
        this.updateParents(item.children);
        item.checked =
          item.children.filter(f => f.checked).length === item.children.length;
        item.expandable = item.children.filter(f => f.checked).length > 0;
      }
    });
  }

  updateMainItems(items: TreeviewNodeType[], updatedItem?: TreeviewNodeType) {
    if (updatedItem) {
      items?.forEach(item => {
        if (item?.children?.length > 0) {
          this.updateMainItems(item.children, updatedItem);
        } else {
          this.updateValue(item, updatedItem);
        }
      });
    } else {
      items?.forEach(item => {
        if (item?.children?.length > 0) {
          this.updateMainItems(item.children);
        } else {
          this.clonedItemsForSearch?.forEach(mainIt => {
            if (mainIt?.children?.length > 0) {
              this.updateMainItems(mainIt.children, item);
            } else {
              this.updateValue(mainIt, item);
            }
          });
        }
      });
    }
  }

  updateValue = (item: TreeviewNodeType, updatedItem?: TreeviewNodeType) => {
    if (item.value === updatedItem.value) {
      item.checked = updatedItem.checked;
    }
  };

  getSelectedLeafNodes = (items: TreeviewNodeType[]) => {
    var filteredItems = items.map(child => {
      if (child.children && child.children.length > 0) {
        return this.getSelectedLeafNodes(child.children);
      }

      if (child.checked) return child;
      return null;
    });

    return filteredItems.flat().filter(f => f !== null && f !== undefined);
  };

  getSelectedValues = (selectedItems: TreeviewNodeType[]) => {
    var selectedValues = [];
    selectedItems.forEach(item => {
      if (item?.children?.length === 0 && item.checked) {
        selectedValues.push(item.value);
      } else {
        var selectedLeafNodes = this.getSelectedLeafNodes(item.children);
        selectedValues = [
          ...selectedValues,
          ...selectedLeafNodes.map(c => c.value)
        ];
      }
    });

    return selectedValues;
  };

  onSelectedItemsChanged() {
    this.updateParents(this.currentItems);
    this.updateMainItems(this.currentItems);
    this.updateParents(this.clonedItemsForSearch);
    this.updateSelectedItems();
    this.check.emit(this.getSelectedValues(this.selectedItems));
  }

  filterItems(searchText: string) {
    if (!searchText || searchText.trim() === '') {
      return this.clonedItemsForSearch;
    }

    const getNodes = (result: TreeviewNodeType[], object: TreeviewNodeType) => {
      if (
        object.label
          .toLocaleLowerCase()
          .includes(searchText.toLocaleLowerCase()) ||
        object.value
          .toLocaleLowerCase()
          .includes(searchText.toLocaleLowerCase())
      ) {
        result.push(object);
        return result;
      }
      if (Array.isArray(object.children)) {
        const nodes = object.children.reduce(getNodes, []);
        if (nodes.length) result.push({ ...object, children: nodes });
      }
      return result;
    };

    return this.clonedItemsForSearch.reduce(getNodes, []);
  }

  @Listen('itemsUpdated')
  onItemsUpdated(event: any) {
    let config = this.config as TreeSelectConfigType;
    // only one tree in single select so no need to loop over children
    if (config.singleSelect) {
      this.check.emit(event.detail.value);
      return;
    }
    this.onSelectedItemsChanged();
  }

  /**
   * stencil won't trigger a rerender if the array is modified
   * To work around this a new array is created by cloning the passed in items
   */
  updateCurrentItems(items) {
    this.currentItems = JSON.parse(JSON.stringify(items));
  }

  @Listen('treeSelectExpand')
  onToggleExpand(event: CustomEvent<ToggleExpandEventType>) {
    this.toggleExpand.emit({
      expanded: event.detail.expanded,
      value: event.detail.targetNode.value
    });
  }

  onSearchTextChange = e => {
    this.searchText = e?.target?.value;
    const filteredItems = this.filterItems(e?.target?.value);
    this.updateCurrentItems(filteredItems);
    this.searchChange.emit(this.searchText);
  };

  onSortChange = e => {
    if (this.sortKey === e?.target.id) {
      this.sortAsc = !this.sortAsc;
    } else {
      this.sortKey = e?.target?.id;
      this.sortAsc = this.sortKey === 'count' ? false : true;
    }

    const sortedData = sortData(
      this.currentItems,
      this.sortAsc ? 'asc' : 'desc',
      this.sortKey
    );
    this.updateCurrentItems(sortedData);
    this.sortChange.emit({
      sortKey: this.sortKey,
      sortAsc: this.sortAsc
    });
  };

  onSelectAll = () => {
    this.setCheckedStatus(this.currentItems, true);
    this.updateParents(this.currentItems);
    this.updateMainItems(this.currentItems);
    this.updateParents(this.clonedItemsForSearch);
    this.updateSelectedItems();
    this.check.emit(this.getSelectedValues(this.selectedItems));
  };

  setCheckedStatus(items: TreeviewNodeType[], checked: boolean) {
    items.forEach(item => {
      item.checked = checked;
      if (item?.children?.length > 0) {
        this.setCheckedStatus(item.children, checked);
      }
    });
  }

  onItemsScroll = (event: UIEvent) => {
    this.itemsScroll.emit(event);
  };

  onDeselectAll = () => {
    this.setCheckedStatus(this.currentItems, false);
    this.updateParents(this.currentItems);
    this.updateMainItems(this.currentItems);
    this.updateParents(this.clonedItemsForSearch);
    this.updateSelectedItems();
    this.check.emit(this.getSelectedValues(this.selectedItems));
  };

  render() {
    const config = this.config as TreeSelectConfigType;

    return (
      <Host>
        <div>
          {!config.hideSearch ? (
            <div class="uui-tree-select-search-container">
              <uui-input-title>{config.searchLabel}</uui-input-title>
              <uui-search-input
                placeholder={config.searchPlaceholder}
                onTextchange={this.onSearchTextChange.bind(this)}
              />
              <div class="divider" />
            </div>
          ) : null}
          {!config.hideSelectAll && !config.singleSelect ? (
            <div class="uui-tree-select-options-container">
              <div>
                <uui-button
                  variant="ghost"
                  onClick={this.onSelectAll.bind(this)}
                >
                  {config.selectAllLabel}
                </uui-button>
                <span class="pipe">|</span>
                <uui-button
                  variant="ghost"
                  class="uui-link treeview-select deselect-all"
                  onClick={this.onDeselectAll.bind(this)}
                >
                  {config.deselectAllLabel}
                </uui-button>
              </div>
            </div>
          ) : null}
          {!config.hideSort ? (
            <div class="uui-tree-select-header-container">
              <div
                class={`uui-tree-select-header${
                  this.sortKey === config.sortProperty ? '-active' : ''
                }`}
              >
                <button
                  onClick={this.onSortChange.bind(this)}
                  class="uui-button-header-button"
                >
                  <p
                    id={config.sortProperty}
                    class={`uui-extra-small bold ${
                      this.sortKey === config.sortProperty ? 'active' : ''
                    }`}
                  >
                    {config.headingLabel}
                  </p>
                </button>
                <uui-icon
                  type={
                    this.sortKey === config.sortProperty && this.sortAsc
                      ? 'chevron-up'
                      : 'chevron-down'
                  }
                  color={
                    this.sortKey === config.sortProperty
                      ? 'var(--uui-gray-01)'
                      : 'var(--uui-teal-dark-active)'
                  }
                  slot="icon"
                  class={`uui-icon-header-icon ${
                    this.sortKey === config.sortProperty ? 'active' : ''
                  }`}
                />
              </div>
              <div
                class={`uui-tree-select-header${
                  this.sortKey === 'count' ? '-active' : ''
                }`}
              >
                <button
                  onClick={this.onSortChange.bind(this)}
                  class="uui-button-header-button"
                >
                  <p
                    id="count"
                    class={`uui-extra-small bold ${
                      this.sortKey === 'count' ? 'active' : ''
                    }`}
                  >
                    {config.countLabel}
                  </p>
                </button>
                <uui-icon
                  type={
                    this.sortKey === 'count' && this.sortAsc
                      ? 'chevron-up'
                      : 'chevron-down'
                  }
                  color={
                    this.sortKey === 'count'
                      ? 'var(--uui-gray-01)'
                      : 'var(--uui-teal-dark-active)'
                  }
                  slot="icon"
                  class={`uui-icon-header-icon ${
                    this.sortKey === 'count' ? 'active' : ''
                  }`}
                />
              </div>
            </div>
          ) : (
            <div class="uui-tree-select-header-container">
              <p class="uui-extra-small bold">{config.headingLabel}</p>
              <p class="uui-extra-small bold">{config.countLabel}</p>
            </div>
          )}
          <div
            onScroll={this.onItemsScroll}
            class="uui-tree-select-items-container"
          >
            {this.currentItems.map(item => {
              return (
                <div key={`${this.sortKey}-${this.sortAsc}`}>
                  <uui-tree-select-item
                    searchText={this.searchText}
                    item={item}
                    groupName={config.name}
                    singleSelect={config.singleSelect}
                    isTreeSelectCom={true}
                    showChild={item.expandable}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </Host>
    );
  }
}
